import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/shared/lib/utils';

/**
 * Layout for the controls above a table.
 *
 * A layout shell, not a filter framework: each page composes its own filter
 * controls as children, because a filter set is domain knowledge and a generic
 * `filters={[…]}` config would need to grow a new option for every screen (§44).
 *
 * The "Clear filters" button is centralised because that behaviour genuinely is
 * identical everywhere, and because a user who has filtered themselves into an
 * empty table needs an obvious way out.
 */
export function DataTableToolbar({
  children,
  actions,
  hasActiveFilters = false,
  onResetFilters,
  className,
}: {
  /** Search box, selects, date pickers. */
  children?: React.ReactNode;
  /** End-aligned controls — column visibility, export. */
  actions?: React.ReactNode;
  hasActiveFilters?: boolean;
  onResetFilters?: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}

      {hasActiveFilters && onResetFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onResetFilters}
          className="h-8 gap-1 px-2 text-xs"
        >
          <X className="size-3.5" aria-hidden />
          Clear filters
        </Button>
      )}

      {actions && <div className="ms-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}
